import { useCallback, useEffect, useMemo } from "react";
import { filter, flatMap, includes, intersection, isEmpty, isEqual, isNil, map, sortBy, uniq } from "lodash";
import { AiccLessonLevel, AiccLessonRef } from "types/model/aiccLearningAssets";
import { useLearningAssetsStore } from "../../useLearningAssetsStore";
import { toLessonOption, toLevelOption } from "./lessonOptions";

const EMPTY_FILTERS: AiccLessonLevel[] = [];

export function useLessonEntryFilters<T extends AiccLessonRef>(items: T[], filters: AiccLessonLevel[] = EMPTY_FILTERS) {
  const selectedLevels = useLearningAssetsStore((state) => state.selectedLevels);
  const selectedLessons = useLearningAssetsStore((state) => state.selectedLessons);
  const setSelectedLevels = useLearningAssetsStore((state) => state.setSelectedLevels);
  const setSelectedLessons = useLearningAssetsStore((state) => state.setSelectedLessons);

  const levelOptions = useMemo(() => map(filters, ({ level }) => toLevelOption(level)), [filters]);

  const getLessonsForLevels = useCallback(
    (levels: number[]) => {
      const groups = isEmpty(levels) ? filters : filter(filters, ({ level }) => includes(levels, level));
      return sortBy(uniq(flatMap(groups, ({ lessons }) => lessons)));
    },
    [filters]
  );

  const lessonOptions = useMemo(() => map(getLessonsForLevels(selectedLevels), toLessonOption), [
    getLessonsForLevels,
    selectedLevels,
  ]);

  const getLessonOptions = useCallback(
    (level?: number) => map(getLessonsForLevels(isNil(level) ? [] : [level]), toLessonOption),
    [getLessonsForLevels]
  );

  useEffect(() => {
    if (isEmpty(filters)) {
      return;
    }
    const kept = intersection(selectedLessons, getLessonsForLevels(selectedLevels));
    if (!isEqual(kept, selectedLessons)) {
      setSelectedLessons(kept);
    }
  }, [filters, getLessonsForLevels, selectedLessons, selectedLevels, setSelectedLessons]);

  const entries = useMemo(
    () =>
      filter(
        items,
        ({ level, lesson }) =>
          (isEmpty(selectedLevels) || includes(selectedLevels, level)) &&
          (isEmpty(selectedLessons) || includes(selectedLessons, lesson))
      ),
    [items, selectedLessons, selectedLevels]
  );

  return {
    entries,
    levelOptions,
    lessonOptions,
    getLessonOptions,
    selectedLevels,
    selectedLessons,
    setSelectedLevels,
    setSelectedLessons,
  };
}
